import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import axios from "axios";
import serverURL from "../config";
import ConfirmationModal from "./ConfirmationModal";
import ModalShell from "./ModalShell";

const CategoryModal = ({ title, category, closeCategoryModal, fetchCategory }) => {

    const [formData, setFormData] = useState({});

    useEffect(() => {
        setFormData({
            category_name: category?.category_name || '',
            description: category?.description || '',
            is_active: category ? !!category.is_active : true,
        });
    }, [category]);


    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };


    const handleClose = () => {
        setFormData({});
        closeCategoryModal();
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        if (title !== "Delete Category") {
            const trimmedName = formData.category_name?.trim();
            if (!trimmedName || !formData.description) {
                toast.error('Please fill in all required fields.');
                return;
            }
            // Update formData with the trimmed name
            formData.category_name = trimmedName;
        }

        const token = localStorage.getItem('token');
        try {
            const headers = {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            };

            if (title === "Add Category") {
                await axios.post(`${serverURL}/categories`, formData, { headers });
                toast.success('Category created successfully!');
            } else if (title === "Edit Category") {
                await axios.put(`${serverURL}/categories/${category.id}`, formData, { headers });
                toast.success('Category edited successfully!');
            } else if (title === "Delete Category") {
                await axios.delete(`${serverURL}/categories/${category.id}`, { headers });
                toast.success('Category deleted successfully!');
            }
            if (fetchCategory) {
                fetchCategory();
            }
            handleClose();
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred.';
            toast.error(errorMessage);
            console.error('Error details:', error.response || error.message);
        }
    };

    // For delete, show ConfirmationModal instead of the form modal
    if (title === 'Delete Category') {
        return (
            <ConfirmationModal
                isOpen={true}
                title="Delete Category"
                message="Are you sure you want to delete this category?"
                onConfirm={handleSubmit}
                onCancel={handleClose}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-500 hover:bg-red-600"
            />
        );
    }

    return (
        <ModalShell title={title} onClose={handleClose}>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="category-name" className="block text-gray-700 text-sm font-bold mb-2">
                        Category Title (max 50)
                    </label>
                    <input
                        id="category-name"
                        type="text"
                        name="category_name"
                        placeholder="Enter Category title"
                        value={formData.category_name || ''}
                        onChange={handleChange}
                        maxLength={50}
                        className={`w-full p-3 rounded-lg border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all ${!formData.category_name ? 'border-campus-error' : 'border-campus-border'}`}
                    />
                </div>
                <div className="mb-4">
                    <label htmlFor="category-description" className="block text-campus-text-primary text-sm font-bold mb-2">
                        Category Description
                    </label>
                    <input
                        id="category-description"
                        type="text"
                        name="description"
                        placeholder="Enter Category Description"
                        value={formData.description || ''}
                        onChange={handleChange}
                        className={`w-full p-3 rounded-lg border bg-campus-bg text-campus-text-primary focus:outline-none focus:ring-2 focus:ring-campus-primary focus:border-transparent transition-all ${!formData.description ? 'border-campus-error' : 'border-campus-border'}`}
                    />
                </div>
                <div className="mb-4 flex items-center">
                    <label htmlFor="category-active" className="text-gray-700 text-sm font-bold mr-2">Active</label>
                    <input id="category-active" type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex justify-end pt-4 border-t border-campus-border">
                    <button
                        type="submit"
                        className="bg-campus-primary hover:bg-campus-primary-dark text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-sm"
                    >
                        Save
                    </button>
                </div>
            </form>
        </ModalShell>
    );
};

CategoryModal.propTypes = {
    title: PropTypes.string.isRequired,
    category: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        category_name: PropTypes.string,
        description: PropTypes.string,
        is_active: PropTypes.bool,
    }),
    closeCategoryModal: PropTypes.func.isRequired,
    fetchCategory: PropTypes.func.isRequired,
};

CategoryModal.defaultProps = {
    category: null,
};

export default CategoryModal;